import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { awsIntegratedKeys, integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import AWSConnectForm from "./AWSConnectForm";
import aws_step_1 from "./images/aws_step_1.png";
import aws_step_3 from "./images/aws_step_3.png";
import aws_step_4 from "./images/aws_step_4.png";
import PermissionsSummary from "./PermissionSummary";

const AWSStandaloneAccounts = ({
  awsRegionOptions,
  userPermissions,
  guideMode,
}) => {
  const queryClient = useQueryClient();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("aws");
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [breezeAccountID, setBreezeAccountID] = useState("");
  const [integratedAccounts, setIntegratedAccounts] = useState({});
  const [externalId, setExternalId] = useState("");

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const {
    registerAWSIntegration,
    checkAWSRootAccountIsIntegrated,
    fetchAWSAccountID,
    fetchAllAWSAccounts,
    getCloudFormationFolder,
    generateAWSExternalId,
  } = useAPI();

  const colors = useColors();
  const { data: CloudFormationFolder } = useQuery({
    queryKey: awsIntegratedKeys.cloudFormationFolder(),
    queryFn: getCloudFormationFolder,
    select: (res) => res.data,
  });

  const breezeDefaultStackName = "BreezePlatform";

  const quickCreateUrl = (region) => {
    return (
      `https://${region}.console.aws.amazon.com/cloudformation/home?region=${region}#/stacks/quickcreate?` +
      `templateURL=https%3A%2F%2Fbreeze-security-public.s3.eu-west-1.amazonaws.com%2F${CloudFormationFolder}%2FBreezePlatform.json&` +
      "stackName=" +
      breezeDefaultStackName +
      "&" +
      "param_BreezeRoleName=BreezeSecurity-Role&" +
      "param_BreezeAWS=" +
      breezeAccountID
    );
  };
  useEffect(() => {});

  const handleLinkClick = () => {
    // Open the IAM console in a new tab with the specified default values
    window.open(quickCreateUrl(selectedRegion?.code || ""), "_blank");
  };

  const onSubmit = (values) => {
    const account_id = values.roleARN.split(":")[4];
    const accountExists = integratedAccounts.hasOwnProperty(account_id);
    if (accountExists) {
      Toast.warning("Account already exist");
      setLoading(false);
      return;
    }

    setLoading(true);
    registerAWSIntegration(
      values.roleARN.trim(),
      values.region,
      "", // legacy nickname place - not used
      false,
      externalId
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("AWS account integrated successfully");
        setLoading(false);
        setIntegratedAccounts(response.data);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
          setLoading(false);
        } else {
          Toast.error("An error occurred while processing the request.");
          setLoading(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    checkAWSRootAccountIsIntegrated().then((response) => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAWSAccountID(), generateAWSExternalId()])
      .then(([accountResp, externalIdResp]) => {
        setBreezeAccountID(accountResp.data.account_id);
        setExternalId(externalIdResp.data.external_id);
      })
      .finally(() => {
        setLoading(false);
      });

    if (isIntegrated) {
      fetchAllAWSAccounts().then((response) => {
        setLoading(false);
        setIntegratedAccounts(response.data);
      });
    }
  }, [isIntegrated]);

  const handleRegionChange = (newValue) => {
    if (!newValue) {
      setSelectedRegion(null);
      return;
    }
    setSelectedRegion(newValue);
  };

  if (loading || isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box mt={2}>
      <Box mb="20px">
        <Grid container spacing={2} direction={"row"}>
          <Grid item>
            <SecondaryButton onClick={handleExpandAll}>
              Expand All Screenshots
            </SecondaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton onClick={handleCollapseAll}>
              Collapse All Screenshots
            </SecondaryButton>
          </Grid>
        </Grid>
      </Box>
      <GuidelineStepByStep>
        <GuidelineStep title="Sign in to the appropriate AWS account"></GuidelineStep>
        <GuidelineStep title="Ensure you have the necessary AWS permissions">
          <Accordion sx={{ width: "700px", backgroundColor: "#f5f5f5" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {" "}
              <Typography variant="subtitle2" color={colors.primary_darker}>
                AWS required permissions
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {userPermissions.map((permission, index) => (
                <Typography key={index}>{permission}</Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        </GuidelineStep>
        <GuidelineStep title="Run the following AWS CloudFormation to create an IAM Role for Breeze Security">
          <>
            <Box>
              <Typography variant={"body2"} mb={2}>
                Select the AWS region where you want to create the stack.
              </Typography>
              <Autocomplete
                sx={{
                  color: colors.text_primary,
                  borderRadius: "10px",
                  "& .MuiAutocomplete-clearIndicator": {
                    display: "none",
                  },
                }}
                id="aws-region-autocomplete"
                options={awsRegionOptions}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={selectedRegion}
                onChange={(event, newValue) => handleRegionChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="AWS Region"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "50px",
                        width: "300px",
                        "& fieldset": {
                          borderColor: colors.divider2,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.divider2,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.divider2,
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "14px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "14px",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "14px",
                    },
                  },
                }}
                componentsProps={{
                  popupIndicator: {
                    disableRipple: true,
                  },
                }}
              />
            </Box>
            <PrimaryButton
              sx={{
                height: "36px",
                borderRadius: "10px",
                width: "260px",
                textTransform: "none",
              }}
              onClick={handleLinkClick}
              disabled={!selectedRegion || guideMode}
            >
              Deploy CloudFormation Stack
            </PrimaryButton>
            <PermissionsSummary />
          </>
        </GuidelineStep>
        <GuidelineStep title="Edit the values if you wish and click Next">
          <Typography variant={"body2"}>
            Stack name: <strong>{breezeDefaultStackName}</strong> (default)
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant={"body2"}>
              BreezeAWS: <strong>{breezeAccountID}</strong>
            </Typography>{" "}
            <CustomCopyButton
              copyTitle={"AWS Account ID"}
              copyText={breezeAccountID}
            />
          </Box>
          <Typography variant={"body2"}>
            BreezeRoleName: Name of the role that will be created for Breeze to
            assume (default: <strong>BreezeSecurity-Role</strong>)
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant={"body2"}>
              External ID: <strong>{externalId}</strong>
            </Typography>
            <CustomCopyButton copyTitle={"External ID"} copyText={externalId} />
          </Box>
          <Typography variant={"body2"} color={colors.warning_darker}>
            Important: Do not modify the External ID.
            <br /> It is used to ensure that the Role created is only assumable
            by Breeze Security.
          </Typography>

          <ScreenshotAccordion
            images={[
              {
                file: aws_step_1,
                altext: "AWS CloudFormation Filling",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        <GuidelineStep title="Down below on Capabilities section accept the terms">
          <ScreenshotAccordion
            itemId={2}
            images={[
              {
                file: aws_step_3,
                altext: "AWS CloudFormation Creation",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        <GuidelineStep title="Click Create Stack"></GuidelineStep>
        <GuidelineStep title="Wait for the stack to be created and copy the Role ARN from output"></GuidelineStep>
        <GuidelineStep title="Fill the required values in the form below and click Connect">
          <ScreenshotAccordion
            itemId={3}
            images={[
              {
                file: aws_step_4,
                altext: "AWS Role ARN",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <AWSConnectForm
            onSubmit={onSubmit}
            awsRegions={awsRegionOptions}
            guideMode={guideMode}
          />
        </GuidelineStep>
      </GuidelineStepByStep>
    </Box>
  );
};

export default AWSStandaloneAccounts;
